/**
 * EL AVISO DEL ÁCIDO HIALURÓNICO (administración, 21/10/2026;
 * backend/services/hialuronidasa.py). Solo para el equipo: lo que dice no se
 * guarda en ningún sitio del presupuesto ni sale en ningún papel o enlace.
 *
 * Lo enseñan tres sitios, con el mismo endpoint
 * (`GET /api/budgets/aviso-hialuronico/{paciente}`):
 *
 *   · «Nuevo presupuesto» y la edición de uno que no se ha entregado
 *     (NewBudgetDialog): con «¿Añadir hialuronidasa?» si no lleva la línea, y
 *     solo informativo si ya la lleva.
 *   · La confirmación del paquete online (WhatsAppPaqueteButton) y el menú del
 *     PDF «Paquete» (PaquetePdfButton): informativo, y diciendo claro si
 *     NINGUNA hoja lleva la hialuronidasa. No frena: tiene que verse.
 *
 * El marcador es «▸»: ni emojis ni iconos que no se pintan en todas partes.
 */
import React, { useEffect, useState } from 'react';
import { api, formatEUR } from '../lib/api';
import { esLineaDeHialuronidasa } from '../lib/marcasPaciente';
import { Button } from './ui/button';

/** ¿Alguna línea de este presupuesto es la de la hialuronidasa? */
export function llevaLaLinea(budget) {
  return (budget?.items || []).some((it) => esLineaDeHialuronidasa(it?.concept));
}

/** ¿El evolutivo la dejó sin importe y sigue a 0 €? (como el servidor) */
export function importePendiente(budget) {
  return budget?.hialuronidasa_importe_pendiente === true
    && (budget?.items || []).some((it) => esLineaDeHialuronidasa(it?.concept) && !(Number(it?.amount) > 0));
}

/**
 * La etiqueta pequeña «Hialuronidasa sin importe» junto al estado del
 * presupuesto (segunda revisión del 21/10/2026): que se vea ANTES de intentar
 * imprimirlo o mandarlo, que el servidor no lo deja salir así.
 */
export function EtiquetaHialuronidasaSinImporte({ budget, testId }) {
  if (!importePendiente(budget)) return null;
  return (
    <span
      className="inline-flex items-center shrink-0 px-2 py-0.5 rounded-full border text-[11px] font-semibold bg-red-50 text-red-800 border-red-300"
      title="El evolutivo la dejó a 0 €: edita el presupuesto y escribe el importe (o quita la línea) para poder imprimirlo o mandarlo."
      data-testid={testId}
    >
      Hialuronidasa sin importe
    </span>
  );
}

/**
 * Pide el aviso de un paciente. `activo` falso no pide nada (y vacía lo que
 * hubiera). Devuelve null (nada que avisar o aún no se sabe), el aviso si lo
 * lleva, o {error: true} si no se ha podido mirar. Cambiar `recarga` lo vuelve
 * a pedir (el menú del PDF, cada vez que se abre).
 *
 * Lo que se devuelve es SOLO la respuesta de la petición de ahora (segunda
 * revisión del 21/10/2026): al reabrir el menú del PDF, o al cambiar de
 * paciente, el aviso de la vez anterior no se llega a pintar ni un instante.
 */
export function useAvisoHialuronico({ patientId, budgetId = null, activo = true, recarga = 0 }) {
  const clave = `${patientId || ''}|${budgetId || ''}|${activo ? 1 : 0}|${recarga}`;
  const [respuesta, setRespuesta] = useState({ clave: null, aviso: null });
  useEffect(() => {
    if (!activo || !patientId) return undefined;
    let vigente = true;
    const opciones = budgetId ? { params: { budget_id: budgetId } } : undefined;
    Promise.resolve()
      .then(() => api.get(`/api/budgets/aviso-hialuronico/${encodeURIComponent(patientId)}`, opciones))
      .then((r) => {
        const d = r?.data;
        const lleva = d && !Array.isArray(d) && d.lleva === true;
        if (vigente) setRespuesta({ clave, aviso: lleva ? d : null });
      })
      .catch(() => { if (vigente) setRespuesta({ clave, aviso: { error: true } }); });
    return () => { vigente = false; };
  }, [clave, patientId, budgetId, activo]);
  return respuesta.clave === clave ? respuesta.aviso : null;
}

/** Lo que dice el paquete de la hialuronidasa: qué hojas la llevan, o que
 *  ninguna; y cuáles la llevan SIN importe (`importe_pendiente`), que esas no
 *  salen hasta escribirlo. */
function TextoDeLasHojas({ hojas }) {
  const con = hojas.filter((h) => h.lleva_linea).map((h) => h.number).filter(Boolean);
  const sinImporte = hojas.filter((h) => h.importe_pendiente).map((h) => h.number).filter(Boolean);
  const pendientes = sinImporte.length > 0 && (
    <div className="text-xs font-semibold text-red-900" data-testid="aviso-hialuronico-hojas-sin-importe">
      {`▸ ${sinImporte.join(', ')} lleva la hialuronidasa sin importe: edítalo y escribe el importe antes de imprimirlo o mandarlo.`}
    </div>
  );
  if (!con.length) {
    return (
      <div className="text-sm font-bold text-red-900" data-testid="aviso-hialuronico-ninguna-hoja">
        ▸ Ninguna hoja lleva hialuronidasa: edita el presupuesto para añadirla, o envía igualmente.
      </div>
    );
  }
  return (
    <>
      <div className="text-xs text-red-900" data-testid="aviso-hialuronico-hojas">
        {`La hialuronidasa va en ${con.join(', ')}.`}
      </div>
      {pendientes}
    </>
  );
}

/**
 * El recuadro.
 *   modo 'pregunta'  ¿Añadir hialuronidasa? Sí / No (onSi, onNo).
 *   modo 'info'      ya la lleva: solo lo dice (onCerrar, opcional).
 *   modo 'paquete'   con `hojas` [{number, lleva_linea}]: qué hojas la llevan.
 *   compacto         para el menú del PDF (sin rótulo grande).
 */
export default function AvisoHialuronico({
  aviso, modo = 'pregunta', onSi, onNo, onCerrar, hojas = null, compacto = false,
  testId = 'budget-aviso-hialuronico',
}) {
  if (!aviso) return null;
  if (aviso.error) {
    return (
      <div className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900" data-testid={`${testId}-error`}>
        No se ha podido comprobar si lleva ácido hialurónico: míralo en sus Datos clínicos.
      </div>
    );
  }
  const donde = (aviso.donde || '').trim();
  const casilla = aviso.casilla === false ? 'en «No»' : 'sin marcar';
  const ya = Array.isArray(aviso.ya_en_presupuestos) ? aviso.ya_en_presupuestos : [];
  const conImporte = typeof aviso.importe === 'number';
  return (
    <div
      role="alert"
      className={`rounded-lg border-2 border-red-600 bg-red-50 ${compacto ? 'p-2.5 space-y-1.5' : 'p-4 space-y-2'}`}
      data-testid={testId}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wider text-red-800">
        Aviso para el equipo · no se imprime
      </div>
      <div className={`${compacto ? 'text-sm' : 'text-base'} font-bold text-red-900`} data-testid={`${testId}-titulo`}>
        {aviso.dudoso ? '▸ Puede llevar ácido hialurónico' : '▸ Lleva ácido hialurónico'}
      </div>
      <div className="text-sm text-red-900" data-testid={`${testId}-donde`}>
        <span className="font-semibold">Dónde: </span>
        {donde || 'no consta dónde en sus Datos clínicos'}
      </div>
      {aviso.dudoso && (
        <div className="text-xs text-red-900" data-testid={`${testId}-dudoso`}>
          {`La casilla de Datos clínicos está ${casilla}, pero consta escrito lo de arriba. Revísalo en su ficha.`}
        </div>
      )}
      {modo === 'paquete' && Array.isArray(hojas) && <TextoDeLasHojas hojas={hojas} />}
      {modo !== 'paquete' && ya.length > 0 && (
        <div className="text-xs text-red-900" data-testid={`${testId}-ya`}>
          {`La hialuronidasa ya va en ${ya.map((b) => b.number).filter(Boolean).join(', ') || 'otro presupuesto'} de este paciente.`}
        </div>
      )}
      {modo === 'info' && (
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <span className="text-sm font-semibold text-red-900" data-testid={`${testId}-ya-la-lleva`}>
            Este presupuesto ya lleva la línea de hialuronidasa.
          </span>
          {onCerrar && (
            <Button type="button" size="sm" variant="outline" onClick={onCerrar} data-testid={`${testId}-entendido`}>
              Entendido
            </Button>
          )}
        </div>
      )}
      {modo === 'pregunta' && (
        <>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <span className="text-sm font-semibold text-red-900">¿Añadir hialuronidasa?</span>
            <Button type="button" size="sm" className="bg-red-700 hover:bg-red-800 text-white" onClick={onSi} data-testid={`${testId}-si`}>
              Sí
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={onNo} data-testid={`${testId}-no`}>
              No
            </Button>
          </div>
          <div className="text-xs text-red-900" data-testid={`${testId}-importe`}>
            {conImporte
              ? `Se añade «${aviso.concepto}» con ${aviso.importe_texto || formatEUR(aviso.importe)} del Catálogo de cirugías; puedes cambiar el importe.`
              : (aviso.aviso_importe || 'Sin importe en el Catálogo de cirugías: escríbelo a mano.')}
          </div>
        </>
      )}
    </div>
  );
}
